import React from 'react';
import { RichTextNode } from '../interface/interface';

const BULLET_PREFIX_RE = /^[•\-*]\s*/;
const ORDERED_PREFIX_RE = /^\d+[.)]\s*/;
const BULLET_LINE_RE = /^[•\-*]\s+/;
const ORDERED_LINE_RE = /^\d+[.)]\s+/;

const HEADING_CLASSES: Record<number, string> = {
  1: 'text-3xl font-bold',
  2: 'text-2xl font-bold',
  3: 'text-xl font-semibold',
  4: 'text-lg font-semibold',
  5: 'text-base font-semibold',
  6: 'text-sm font-semibold',
};

type ParsedList = { type: 'unordered' | 'ordered'; items: string[] };

const listClassName = (type: 'unordered' | 'ordered'): string =>
  type === 'ordered'
    ? 'list-decimal ml-6 space-y-2 text-sm'
    : 'list-disc ml-6 space-y-2 text-sm';

const normalizeWhitespace = (s: string): string =>
  s?.replace(/[\t\u00A0]/g, ' ').trim();

const parseFlatList = (text: string): ParsedList | null => {
  if (!text) return null;

  const lines = text
    ?.split(/\r?\n/)
    ?.map((l) => normalizeWhitespace(l))
    ?.filter(Boolean);

  if (lines?.length >= 2 && lines?.every((l) => BULLET_LINE_RE.test(l))) {
    return {
      type: 'unordered',
      items: lines.map((l) => l.replace(BULLET_LINE_RE, '').trim()),
    };
  }

  if (lines?.length >= 2 && lines?.every((l) => ORDERED_LINE_RE.test(l))) {
    return {
      type: 'ordered',
      items: lines?.map((l) => l.replace(ORDERED_LINE_RE, '').trim()),
    };
  }

  return null;
};
const getParagraphText = (node: RichTextNode): string =>
  (node.children ?? [])
    .map((c) => (c.type === 'text' ? (c.value ?? '') : ''))
    .join('');

const renderListBlock = (key: string, parsed: ParsedList): React.ReactNode => {
  const ListTag = parsed.type === 'ordered' ? 'ol' : 'ul';
  return (
    <ListTag key={key} className={listClassName(parsed.type)}>
      {parsed.items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ListTag>
  );
};

const tryRenderInlineFlatList = (
  text: string,
  groupKey: number,
): React.ReactNode | null => {
  const parsed = parseFlatList(text);
  return parsed ? renderListBlock(`group-${groupKey}`, parsed) : null;
};

const collectSiblingBulletGroup = (
  children: RichTextNode[],
  startIndex: number,
  firstText: string,
): { node: React.ReactNode; nextIndex: number } | null => {
  const isBullet = BULLET_PREFIX_RE.test(firstText);
  const isOrdered = !isBullet && ORDERED_PREFIX_RE.test(firstText);
  if (!isBullet && !isOrdered) return null;

  const prefixRe = isBullet ? BULLET_PREFIX_RE : ORDERED_PREFIX_RE;
  const items: string[] = [];
  let i = startIndex;

  while (i < children.length) {
    const current = children[i];
    if (current.type !== 'paragraph') break;

    const currentText = getParagraphText(current).trim();
    const matches = isBullet
      ? BULLET_PREFIX_RE.test(currentText)
      : ORDERED_PREFIX_RE.test(currentText);
    if (!matches) break;

    items.push(currentText.replace(prefixRe, '').trim());
    i += 1;
  }

  const parsed: ParsedList = {
    type: isOrdered ? 'ordered' : 'unordered',
    items,
  };
  return { node: renderListBlock(`group-${startIndex}`, parsed), nextIndex: i };
};

export default function ShopifyRichText({
  value,
}: Readonly<{ value: RichTextNode }>) {
  if (!value) return <></>;

  const renderHeading = (node: RichTextNode, key: number): React.ReactNode => {
    const Tag = `h${node.level}` as keyof JSX.IntrinsicElements;
    return React.createElement(
      Tag,
      { key, className: HEADING_CLASSES[node.level as number] },
      node.children?.map((child, i) => renderNode(child, i)),
    );
  };

  const renderParagraph = (
    node: RichTextNode,
    key: number,
  ): React.ReactNode => {
    const combinedText = getParagraphText(node);
    const parsed = parseFlatList(combinedText);
    if (parsed) return renderListBlock(`p-${key}`, parsed);

    return (
      <p key={key} className="whitespace-pre-line">
        {node.children?.map((child, i) => renderNode(child, i))}
      </p>
    );
  };

  const renderText = (node: RichTextNode, key: number): React.ReactNode => {
    let text: React.ReactNode = node.value;
    if (node.bold) text = <strong>{text}</strong>;
    if (node.italic) text = <em>{text}</em>;
    return <React.Fragment key={key}>{text}</React.Fragment>;
  };

  const renderList = (node: RichTextNode, key: number): React.ReactNode => {
    const ListTag = node.listType === 'ordered' ? 'ol' : 'ul';
    const listClass =
      node.listType === 'ordered'
        ? 'list-decimal ml-6 space-y-2'
        : 'list-disc ml-6 space-y-2 text-sm';

    return (
      <ListTag key={key} className={listClass}>
        {node.children?.map((child, i) => renderNode(child, i))}
      </ListTag>
    );
  };

  const renderListItem = (node: RichTextNode, key: number): React.ReactNode => (
    <li key={key}>{node.children?.map((child, i) => renderNode(child, i))}</li>
  );

  const groupBulletParagraphs = (
    children: RichTextNode[],
  ): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    let i = 0;

    while (i < children.length) {
      const node = children[i];

      if (node.type !== 'paragraph') {
        result.push(renderNode(node, i));
        i += 1;
        continue;
      }

      const text = getParagraphText(node).trim();

      const inlineList = tryRenderInlineFlatList(text, i);
      if (inlineList) {
        result.push(inlineList);
        i += 1;
        continue;
      }

      const siblingGroup = collectSiblingBulletGroup(children, i, text);
      if (siblingGroup) {
        result.push(siblingGroup.node);
        i = siblingGroup.nextIndex;
        continue;
      }

      result.push(renderNode(node, i));
      i += 1;
    }

    return result;
  };

  const renderNode = (node: RichTextNode, key: number): React.ReactNode => {
    switch (node.type) {
      case 'root':
        return (
          <div key={key} className="space-y-3 text-sm">
            {groupBulletParagraphs(node.children ?? [])}
          </div>
        );
      case 'heading':
        return renderHeading(node, key);
      case 'paragraph':
        return renderParagraph(node, key);
      case 'text':
        return renderText(node, key);
      case 'list':
        return renderList(node, key);
      case 'list-item':
        return renderListItem(node, key);
      default:
        return null;
    }
  };

  return renderNode(value, 0);
}
